import { useEffect, useRef, useState, useCallback } from 'react';
import { refreshAccessToken } from './api';
import { getAccessToken } from './authSession';

const useWebSocket = (url, options = {}) => {
    const [socket, setSocket] = useState(null);
    const socketRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [lastMessage, setLastMessage] = useState(null);
    const [error, setError] = useState(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttempts = useRef(0);
    const maxReconnectAttempts = options.maxReconnectAttempts || 5;
    const reconnectInterval = options.reconnectInterval || 3000;
    const isRefreshingToken = useRef(false);
    const tokenRefreshInterval = useRef(null);

    // Function to check if token is expired or will expire soon
    const isTokenExpired = useCallback(() => {
        const token = getAccessToken();
        if (!token) return true;
        
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const now = Math.floor(Date.now() / 1000);
            // Consider token expired if it expires within the next 5 minutes
            return payload.exp < (now + 300);
        } catch (e) {
            return true;
        }
    }, []);

    const refreshToken = useCallback(async () => {
        if (isRefreshingToken.current) return null;
        isRefreshingToken.current = true;
        try {
            return await refreshAccessToken();
        } catch (error) {
            console.error('Token refresh failed:', error);
            return null;
        } finally {
            isRefreshingToken.current = false;
        }
    }, []);

    const getValidToken = useCallback(async () => {
        if (isTokenExpired()) {
            return await refreshToken();
        }
        return getAccessToken();
    }, [isTokenExpired, refreshToken]);

    useEffect(() => {
        if (!url) return;

        const connect = async () => {
            try {
                console.log('Attempting WebSocket connection to:', url);
                
                // Check if WebSocket is supported
                if (!window.WebSocket) {
                    setError('WebSocket is not supported in this browser');
                    return;
                }

                // Get a valid token before connecting
                const validToken = await getValidToken();
                if (!validToken) {
                    setError('No valid authentication token available');
                    return;
                }

                const ws = new WebSocket(url);
                let wasOpened = false;
                
                // Set a connection timeout
                const connectionTimeout = setTimeout(() => {
                    if (ws.readyState === WebSocket.CONNECTING) {
                        console.log('WebSocket connection timeout');
                        ws.close();
                        setError('Connection timeout');
                    }
                }, 10000); // 10 second timeout
                
                ws.onopen = () => {
                    clearTimeout(connectionTimeout);
                    wasOpened = true;
                    console.log('WebSocket connected successfully');
                    setIsConnected(true);
                    setError(null);
                    reconnectAttempts.current = 0;
                    
                    // Start periodic token refresh (every 6 hours to refresh before 8-hour expiry)
                    if (tokenRefreshInterval.current) {
                        clearInterval(tokenRefreshInterval.current);
                    }
                    tokenRefreshInterval.current = setInterval(async () => {
                        if (isTokenExpired()) {
                            console.log('Token expiring soon, refreshing...');
                            const newToken = await refreshToken();
                            if (newToken) {
                                console.log('Token refreshed successfully');
                            } else {
                                console.error('Token refresh failed, WebSocket may disconnect soon');
                            }
                        }
                    }, 360 * 60 * 1000); // 6 hours
                };

                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        setLastMessage(data);
                    } catch (err) {
                        console.error('Error parsing WebSocket message:', err);
                    }
                };

                ws.onclose = async (event) => {
                    clearTimeout(connectionTimeout);
                    console.log('WebSocket disconnected:', event.code, event.reason);
                    setIsConnected(false);
                    setSocket(null);
                    
                    // Only treat abnormal closure as auth failure after a successful connection.
                    // Initial 404/unreachable servers also close with 1006 and should not prompt re-login.
                    if ((event.code === 1006 || event.code === 1002) && wasOpened) {
                        console.log('WebSocket closed due to authentication failure, attempting token refresh...');
                        
                        // Try to refresh token and reconnect
                        const newToken = await refreshToken();
                        if (newToken && reconnectAttempts.current < maxReconnectAttempts) {
                            reconnectAttempts.current++;
                            console.log(`Token refreshed, attempting to reconnect... (${reconnectAttempts.current}/${maxReconnectAttempts})`);
                            
                            reconnectTimeoutRef.current = setTimeout(() => {
                                connect();
                            }, reconnectInterval);
                            return;
                        } else {
                            console.error('Token refresh failed or max attempts reached');
                            setError('Authentication failed. Please log in again.');
                            return;
                        }
                    }
                    
                    // Attempt to reconnect if not a normal closure
                    if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
                        reconnectAttempts.current++;
                        console.log(`Attempting to reconnect... (${reconnectAttempts.current}/${maxReconnectAttempts})`);
                        
                        reconnectTimeoutRef.current = setTimeout(() => {
                            connect();
                        }, reconnectInterval);
                    } else if (event.code !== 1000) {
                        console.error('WebSocket connection failed after maximum retry attempts');
                        setError(`Connection failed after ${maxReconnectAttempts} attempts`);
                    }
                };

                ws.onerror = (error) => {
                    clearTimeout(connectionTimeout);
                    console.error('WebSocket error:', error);
                    console.error('WebSocket URL:', url);
                    setError(`WebSocket connection error: ${error.type || 'Unknown error'}`);
                };

                socketRef.current = ws;
                setSocket(ws);
            } catch (err) {
                console.error('Error creating WebSocket:', err);
                setError('Failed to create WebSocket connection');
            }
        };

        connect();

        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (tokenRefreshInterval.current) {
                clearInterval(tokenRefreshInterval.current);
            }
            if (socketRef.current) {
                socketRef.current.close(1000, 'Component unmounting');
            }
        };
    }, [url, maxReconnectAttempts, reconnectInterval, getValidToken, isTokenExpired, refreshToken]);

    const sendMessage = (message) => {
        if (socket && isConnected) {
            socket.send(JSON.stringify(message));
        } else {
            console.warn('WebSocket is not connected');
        }
    };

    return {
        socket,
        isConnected,
        lastMessage,
        error,
        sendMessage
    };
};

export default useWebSocket;